import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { messageForFailedResponse } from '@/lib/formatApiError';
import { machinePicklistPath } from '@/lib/entityPaths';
import {
  formatPicklistCreatedBy,
  formatPicklistWhen,
  parseMachinePicklistList,
  picklistHistoryLabel,
  picklistHistoryState,
  type MachinePicklistAction,
  type MachinePicklistListItem,
} from '@/lib/machinePicklist';
import { refillSessionPath, type RefillSessionListItem } from '@/lib/refillSession';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TableLink, TablePagination } from '@/components/global';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const PAGE_SIZE = 20;

interface MachinePicklistsCardProps {
  machineId: string;
  unmanaged: boolean;
  action: MachinePicklistAction;
  latest: MachinePicklistListItem | null;
  activeRefill: RefillSessionListItem | null;
  generating: boolean;
  onNewPicklist: () => void;
  onContinue: () => void;
}

export function MachinePicklistsCard({
  machineId,
  unmanaged,
  action,
  latest,
  activeRefill,
  generating,
  onNewPicklist,
  onContinue,
}: MachinePicklistsCardProps) {
  const [items, setItems] = useState<MachinePicklistListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const picklistParams = new URLSearchParams({
        machineId,
        page: String(page),
        pageSize: String(PAGE_SIZE),
      });
      const picklistsRes = await apiFetch(`/api/v1/picklists?${picklistParams}`);

      if (picklistsRes.status === 403) {
        setItems([]);
        setTotal(0);
        return;
      }
      if (!picklistsRes.ok) {
        throw new Error(await messageForFailedResponse(picklistsRes, 'Failed to load picklists'));
      }

      const list = parseMachinePicklistList(await picklistsRes.json());
      setItems(list.items);
      setTotal(list.total);
    } catch (err) {
      setItems([]);
      setTotal(0);
      setError(err instanceof Error ? err.message : 'Failed to load picklists');
    } finally {
      setLoading(false);
    }
  }, [machineId, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle>Picklists</CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          {action === 'continue' && latest ? (
            <Button size="sm" onClick={onContinue}>
              Continue picklist
            </Button>
          ) : null}
          {action === 'new' ? (
            <Button size="sm" disabled={generating} onClick={onNewPicklist}>
              {generating ? 'Generating…' : 'New picklist'}
            </Button>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {activeRefill ? (
          <div
            className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-950"
            role="status"
          >
            <p>A refill is in progress. New picklist is available after this session completes.</p>
            <Button size="sm" asChild>
              <Link to={refillSessionPath(activeRefill.id)}>Open refill</Link>
            </Button>
          </div>
        ) : null}

        {error ? <p className="text-sm text-destructive">{error}</p> : null}

        {loading ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Loading picklists…</p>
        ) : items.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {unmanaged
              ? 'This machine is unmanaged. Set a Cloud lifecycle on Edit to pick and refill it.'
              : 'No picklists for this machine yet.'}
          </p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead className="hidden sm:table-cell">Created by</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right"># SKUs</TableHead>
                  <TableHead className="text-right">Units</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => {
                  const state = picklistHistoryState(item.refillSessionId);
                  return (
                    <TableRow key={item.id}>
                      <TableCell>
                        <TableLink to={machinePicklistPath(machineId, item.id)}>
                          {formatPicklistWhen(item.generatedAt)}
                        </TableLink>
                      </TableCell>
                      <TableCell className="hidden text-muted-foreground sm:table-cell">
                        {formatPicklistCreatedBy(item.createdByName)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge variant={state === 'open' ? 'default' : 'secondary'}>
                            {picklistHistoryLabel(state)}
                          </Badge>
                          {item.hasStockShortages ? (
                            <Badge variant="outline">Shortage</Badge>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{item.skuCount}</TableCell>
                      <TableCell className="text-right">{item.totalItemsToPick}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {total > PAGE_SIZE ? (
              <TablePagination
                page={page}
                totalPages={totalPages}
                totalItems={total}
                itemLabel="picklists"
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}
